import React, { useEffect, useRef, useState } from 'react';
import { adminListHeroSlides, adminCreateHeroSlide, adminUpdateHeroSlide, adminDeleteHeroSlide, adminUploadImageFile } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3, X, Upload, Loader2, Image as ImageIcon, EyeOff, Eye } from 'lucide-react';

const empty = { order: 0, heading: '', accent: '', subheading: '', cta: 'Shop Now', href: '/shop', image: '', active: true };

const AdminHeroSlides = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => adminListHeroSlides().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const start = (s) => setEditing(s ? { ...empty, ...s } : { ...empty, order: (items[items.length - 1]?.order || 0) + 1 });
  const close = () => setEditing(null);

  const onUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setUploading(true);
    try {
      const meta = await adminUploadImageFile(file);
      const url = meta.url?.startsWith('http') ? meta.url : (process.env.REACT_APP_BACKEND_URL + meta.url);
      setEditing(prev => ({ ...prev, image: url }));
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing.image) { toast.error('Please add a slide image'); return; }
    const p = { ...editing, order: parseInt(editing.order || 0, 10) };
    try {
      if (editing.id) await adminUpdateHeroSlide(editing.id, p); else await adminCreateHeroSlide(p);
      toast.success('Saved'); close(); load();
    } catch (er) { toast.error(er?.response?.data?.detail || 'Save failed'); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete slide "${s.heading || s.accent || s.id}"?`)) return;
    try { await adminDeleteHeroSlide(s.id); toast.success('Deleted'); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleActive = async (s) => {
    try { await adminUpdateHeroSlide(s.id, { ...s, active: !s.active }); load(); }
    catch { toast.error('Update failed'); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif-display text-4xl">Hero Slides</h1>
          <p className="text-sm text-neutral-600 mt-1">{items.length} slide{items.length === 1 ? '' : 's'} · rotates every 6 seconds on the homepage.</p>
        </div>
        <button onClick={() => start(null)} className="inline-flex items-center gap-2 bg-[#213A60] hover:bg-[#D4A63A] text-white text-xs uppercase tracking-widest px-5 py-2.5 rounded-full" data-testid="add-hero-slide-btn">
          <Plus className="w-3.5 h-3.5" /> Add Slide
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? (
          <div className="md:col-span-3 py-16 text-center text-neutral-500">
            <ImageIcon className="w-8 h-8 mx-auto mb-3 text-[#8A6A22]/50" />
            No hero slides yet. Add one to get started.
          </div>
        ) : items.map(s => (
          <div key={s.id} className="bg-white rounded-xl border border-[#E7DCC7] overflow-hidden">
            <div className="aspect-[16/9] bg-neutral-100 relative">
              {s.image ? <img src={s.image} alt={s.heading} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs uppercase">No image</div>}
              {!s.active && <div className="absolute top-2 left-2 bg-neutral-800/85 text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">Hidden</div>}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#8A6A22]">Order {s.order || 0}</div>
                  <div className="font-serif-display text-lg mt-0.5 line-clamp-1">{s.heading || '—'}</div>
                  <div className="text-sm italic text-[#8A6A22] line-clamp-1">{s.accent}</div>
                  <div className="text-xs text-neutral-500 mt-1 line-clamp-2">{s.subheading}</div>
                  <div className="text-[11px] text-neutral-500 mt-2 font-mono">CTA: {s.cta || '—'} → {s.href || '—'}</div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => toggleActive(s)} className="p-2 rounded-full hover:bg-[#F8F4EC]" title={s.active ? 'Hide' : 'Show'}>{s.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-neutral-400" />}</button>
                  <button onClick={() => start(s)} className="p-2 rounded-full hover:bg-[#F8F4EC]"><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => del(s)} className="p-2 rounded-full hover:bg-rose-50"><Trash2 className="w-4 h-4 text-rose-600" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <>
          <div onClick={close} className="fixed inset-0 bg-black/50 z-50" />
          <aside className="fixed top-0 right-0 h-full w-full sm:w-[520px] bg-white z-50 overflow-y-auto shadow-2xl">
            <div className="px-6 py-5 border-b border-[#E7DCC7] flex items-center justify-between">
              <h3 className="font-serif-display text-2xl">{editing.id ? 'Edit Slide' : 'New Slide'}</h3>
              <button onClick={close}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="px-6 py-5 space-y-4 text-sm">
              <F label="Slide Image *">
                <div className="space-y-2">
                  {editing.image && (
                    <div className="relative">
                      <img src={editing.image} alt="" className="w-full aspect-[16/9] object-cover rounded-lg border border-[#E7DCC7]" />
                      <button type="button" onClick={() => setEditing({ ...editing, image: '' })} className="absolute top-2 right-2 w-7 h-7 bg-white/95 rounded-full flex items-center justify-center hover:bg-white shadow"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input value={editing.image || ''} onChange={e => setEditing({ ...editing, image: e.target.value })} placeholder="Paste image URL or use Upload" className="input flex-1" />
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => onUpload(e.target.files?.[0])} />
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 border border-[#E7DCC7] hover:border-[#D4A63A] hover:text-[#8A6A22] rounded-full px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-60" data-testid="hero-slide-image-upload">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? 'Uploading' : 'Upload'}
                    </button>
                  </div>
                </div>
              </F>
              <F label="Heading (first line)"><input value={editing.heading || ''} onChange={e => setEditing({ ...editing, heading: e.target.value })} className="input" placeholder="Our story can be summed up in two words…" /></F>
              <F label="Accent (italic gold line)"><input value={editing.accent || ''} onChange={e => setEditing({ ...editing, accent: e.target.value })} className="input" placeholder="But God." /></F>
              <F label="Subheading"><textarea rows="3" value={editing.subheading || ''} onChange={e => setEditing({ ...editing, subheading: e.target.value })} className="input !rounded-lg" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="CTA Label"><input value={editing.cta || ''} onChange={e => setEditing({ ...editing, cta: e.target.value })} className="input" placeholder="Shop Now" /></F>
                <F label="CTA Link"><input value={editing.href || ''} onChange={e => setEditing({ ...editing, href: e.target.value })} className="input font-mono" placeholder="/shop" /></F>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Order (lower shows first)"><input type="number" value={editing.order || 0} onChange={e => setEditing({ ...editing, order: e.target.value })} className="input w-32" /></F>
                <label className="flex items-end gap-2 pb-2">
                  <input type="checkbox" checked={!!editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="accent-[#D4A63A]" />
                  <span className="text-[11px] uppercase tracking-widest text-neutral-500">Active</span>
                </label>
              </div>
              <div className="pt-2 flex gap-3">
                <button type="submit" className="flex-1 bg-[#213A60] hover:bg-[#D4A63A] text-white text-xs uppercase tracking-widest px-6 py-2.5 rounded-full">Save</button>
                <button type="button" onClick={close} className="px-6 py-2.5 border border-[#E7DCC7] rounded-full text-xs uppercase tracking-widest">Cancel</button>
              </div>
            </form>
          </aside>
        </>
      )}

      <style>{`.input { width:100%; background:#F8F4EC; border:1px solid #E7DCC7; border-radius:9999px; padding:8px 16px; outline:none; font-size:14px; }
        .input:focus { border-color:#D4A63A; }
        textarea.input, .input.\\!rounded-lg { border-radius: 12px; }`}</style>
    </div>
  );
};

const F = ({ label, children }) => (<label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-500 block mb-1">{label}</span>{children}</label>);

export default AdminHeroSlides;
